import { hydrateAuthFromMe } from "~/composables/useAuth";

/**
 * Hydrate `useAuth().user` from the BFF on boot.
 *
 * Tokens live exclusively in the HttpOnly `__Host-bff` cookie that
 * the Duende BFF controls. The browser only learns the user's stripped
 * claim set via `/bff/auth/user`, which is enough to drive UX decisions
 * (header avatar, conditional `:::auth-gate` rendering).
 *
 * Deliberately NOT awaited and NOT `enforce: 'pre'`. This primes UX-only state
 * (header avatar, `:::auth-gate` blocks) on a hard reload; it is not the route
 * gate. `middleware/auth.global.ts` re-hydrates from the BFF itself when
 * `user.value` is empty (see its header comment — relying on this plugin having
 * finished would reintroduce the post-callback reload race), so protected routes
 * do not depend on this call. Awaiting it here — worse, as a `pre` plugin — would
 * block the whole client mount (WC upgrades, the idle chat widget) on the BFF
 * SSO round-trip, which on a public page is pure dead weight and, when the BFF/
 * IdP is slow, stalls first paint for seconds. Fire-and-forget: boot proceeds,
 * `user` fills in reactively when the claims land.
 */
export default defineNuxtPlugin({
  name: "auth-hydrate",
  setup() {
    // BFF disabled → no session endpoint to hydrate from (the `/bff/*` proxy
    // isn't even registered). Skip so we don't fire a dead `/bff/auth/user`.
    if (!useRuntimeConfig().public.bffEnabled) return;
    const { setUser } = useAuth();
    void hydrateAuthFromMe(setUser);
  },
});
